import {
  type StaffOperationsControlReason,
  type StaffOperationsIntent,
  type StaffOperationsIntentState,
  type StaffOperationsTaskId,
  staffOperationsControlReasons,
  staffOperationsIntentStates,
  staffOperationsTaskIds,
} from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { DataTable } from "../../ui/data-table";
import { SelectField } from "../../ui/fields";
import { Icon } from "../../ui/icon";
import { PageHeader } from "../../ui/page-header";
import { StatusMessage } from "../../ui/status-message";
import { wrapCells } from "../../ui/wrap-cells";
import { useOperationsCapabilities } from "../access";
import { AreaLinks, JobStateBadge, TaskStatusBadge } from "../components/parts";
import { describeOperationsError } from "../errors";
import { formatAge, formatInstant, shortId } from "../format";
import {
  cancelReasonLabels,
  classLabels,
  controlReasonLabels,
  decisionLabels,
  errorClassLabels,
  runnerLabels,
  stateLabels,
  taskLabels,
} from "../labels";
import { operationsKeys, useOperationsJobs, useOperationsSummary } from "../queries";
import { useOperationsRuntime } from "../runtime";

// The Operations page (CHT-761; docs/architecture/milestone-e-jobs-contract.md section 4.3): the queues of the background jobs, the
// jobs themselves, and the three actions that need no SQL: pause, resume and re-run. A supervisor reads; an administrator also acts.
// The page shows ids, codes, counts and times. It never shows an idempotency key (the API has none to give) or a free text, and it never
// judges: the age of the oldest due job is data, not an SLA. Every action is confirmed by the server and answered in words; the page
// reads again after each one. The reason of a pause or a re-run is a closed code chosen in the list below the summary.
//
// Both tables are the kit `DataTable`. Under 720 px of table width each row becomes a card with the column words, so nothing is cut off
// at 390 px. Every action button has its own name: its visible word ("Pause", "Re-run") plus a hidden tail that says which task or which
// job (the first eight characters of the job id, the task and the state: never free text), so a list of the controls of the page has no repeated name.

const isState = (value: string): value is StaffOperationsIntentState =>
  (staffOperationsIntentStates as readonly string[]).includes(value);
const isTask = (value: string): value is StaffOperationsTaskId =>
  (staffOperationsTaskIds as readonly string[]).includes(value);
const isReason = (value: string): value is StaffOperationsControlReason =>
  (staffOperationsControlReasons as readonly string[]).includes(value);

type Summary = NonNullable<ReturnType<typeof useOperationsSummary>["data"]>;
type TaskRow = Summary["tasks"][number];

const sectionHeading = "m-0 font-serif text-[26px] leading-8 font-medium";
const hint = "m-0 text-sm leading-6 text-muted-foreground";
const dash = "—";

// A table row for the kit `DataTable`: the record plus what its cells need to draw (is an action running, what to call). The column lists are
// module constants on purpose: `DataTable` draws a cell with `flexRender`, which treats a cell function as a component. A list made again on
// every render would make a new component for every cell, and the buttons of the table would be thrown away and made again on each render
// (the focused button would lose focus). The rows change; the columns do not.

type TaskView = Readonly<{
  task: TaskRow;
  /** This task has its own pause (a Resume is possible); `false` with `task.paused` means the pause is the one of every task. */
  ownPause: boolean;
  /** The closed reason of the pause, in words. */
  detail: string | undefined;
  busy: boolean;
  onPause: () => void;
  onResume: () => void;
}>;

type JobView = Readonly<{
  job: StaffOperationsIntent;
  busy: boolean;
  onRequeue: () => void;
}>;

const taskColumn = createColumnHelper<TaskView>();
const jobColumn = createColumnHelper<JobView>();

const taskColumns = wrapCells([
  taskColumn.display({
    id: "task",
    header: "Task",
    meta: { rowHeader: true },
    cell: ({ row }) => (
      <>
        <span className="font-semibold">{taskLabels[row.original.task.taskId]}</span>
        <span className="block text-xs font-normal text-muted-foreground">
          {classLabels[row.original.task.workloadClass]}
        </span>
      </>
    ),
  }),
  taskColumn.display({
    id: "waiting",
    header: "Waiting",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.task.counts.PENDING,
  }),
  taskColumn.display({
    id: "running",
    header: "Running",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.task.counts.CLAIMED,
  }),
  taskColumn.display({
    id: "failed",
    header: "Failed",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => (
      <span className={row.original.task.counts.FAILED > 0 ? "text-destructive" : undefined}>
        {row.original.task.counts.FAILED}
      </span>
    ),
  }),
  taskColumn.display({
    id: "done",
    header: "Done",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.task.counts.SUCCEEDED,
  }),
  taskColumn.display({
    id: "cancelled",
    header: "Cancelled",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.task.counts.CANCELLED,
  }),
  taskColumn.display({
    id: "oldest",
    header: "Oldest waiting",
    cell: ({ row }) => formatAge(row.original.task.oldestDueAgeSeconds),
  }),
  taskColumn.display({
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <TaskStatusBadge paused={row.original.task.paused} detail={row.original.detail} />
    ),
  }),
  taskColumn.display({
    id: "action",
    header: "Action",
    cell: ({ row }) => {
      const { task, ownPause, busy, onPause, onResume } = row.original;
      const label = taskLabels[task.taskId];
      return ownPause ? (
        <Button type="button" variant="soft" size="sm" disabled={busy} onClick={onResume}>
          <Icon name="play" />
          Resume <span className="sr-only">{label}</span>
        </Button>
      ) : task.paused ? (
        <span className={hint}>Paused for every task</span>
      ) : (
        <Button type="button" variant="soft" size="sm" disabled={busy} onClick={onPause}>
          <Icon name="pause" />
          Pause <span className="sr-only">{label}</span>
        </Button>
      );
    },
  }),
]);

// No `rowHeader` column here: many jobs share one task, so no cell of a job row names that row alone (the id is in the button names).
const jobColumns = wrapCells([
  jobColumn.display({
    id: "task",
    header: "Task",

    cell: ({ row }) => (
      <>
        <span className="font-semibold">{taskLabels[row.original.job.taskId]}</span>
        <span className="text-xs font-normal text-muted-foreground">
          {" "}
          v{row.original.job.taskVersion}
        </span>
      </>
    ),
  }),
  jobColumn.display({
    id: "state",
    header: "State",
    cell: ({ row }) => (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        <JobStateBadge state={row.original.job.state} />
        {row.original.job.cancelReason === null ? null : (
          <span className="text-xs text-muted-foreground">
            {cancelReasonLabels[row.original.job.cancelReason]}
          </span>
        )}
      </span>
    ),
  }),
  jobColumn.display({
    id: "attempts",
    header: "Attempts",
    cell: ({ row }) => `${row.original.job.attempts} of ${row.original.job.maxAttempts}`,
  }),
  jobColumn.display({
    id: "next",
    header: "Next attempt",
    cell: ({ row }) =>
      row.original.job.state === "PENDING" ? formatInstant(row.original.job.nextAttemptAt) : dash,
  }),
  jobColumn.display({
    id: "error",
    header: "Last error",
    cell: ({ row }) =>
      row.original.job.lastErrorCode === null ? (
        dash
      ) : (
        <>
          <code>{row.original.job.lastErrorCode}</code>
          {row.original.job.errorClass === null ? null : (
            <span className="text-xs text-muted-foreground">
              {" "}
              {errorClassLabels[row.original.job.errorClass]}
            </span>
          )}
        </>
      ),
  }),
  jobColumn.display({
    id: "run",
    header: "Last run",
    cell: ({ row }) =>
      row.original.job.lastRunner === null ? (
        dash
      ) : (
        <>
          {runnerLabels[row.original.job.lastRunner]}
          {row.original.job.lastRunRef === null ? null : (
            <>
              {" "}
              <code>{row.original.job.lastRunRef}</code>
            </>
          )}
        </>
      ),
  }),
  jobColumn.display({
    id: "decision",
    header: "Why nothing happened",
    meta: { headClassName: "min-w-32 whitespace-normal" },
    cell: ({ row }) =>
      row.original.job.latestDecision === null ? (
        dash
      ) : (
        <>
          {decisionLabels[row.original.job.latestDecision.code]}
          {row.original.job.latestDecision.reasonCode === null ? null : (
            <>
              {" "}
              <code>{row.original.job.latestDecision.reasonCode}</code>
            </>
          )}
        </>
      ),
  }),
  jobColumn.display({
    id: "subject",
    header: "Subject",
    cell: ({ row }) => (
      <>
        <code title={row.original.job.subjectType}>{shortId(row.original.job.subjectId)}</code>
        <span className="text-xs text-muted-foreground">
          {" "}
          {row.original.job.subjectType.replaceAll("_", " ")}
        </span>
      </>
    ),
  }),
  jobColumn.display({
    id: "action",
    header: "Action",
    cell: ({ row }) => {
      const { job, busy, onRequeue } = row.original;
      return job.state === "FAILED" || job.state === "CANCELLED" ? (
        <Button type="button" variant="soft" size="sm" disabled={busy} onClick={onRequeue}>
          <Icon name="refresh" />
          Re-run{" "}
          <span className="sr-only">
            job {shortId(job.id)}: {taskLabels[job.taskId]}, {stateLabels[job.state].toLowerCase()}
          </span>
        </Button>
      ) : (
        dash
      );
    },
  }),
]);

export function OperationsPage() {
  useWideLayout();
  const { capabilities, isLoading: rolesLoading } = useOperationsCapabilities();
  const { api } = useOperationsRuntime();
  const queryClient = useQueryClient();
  // CHT-772: a link from the diagnostics may carry the filter in the address (`?state=FAILED`, `?task=<task id>`).
  const [params] = useSearchParams();
  const [state, setState] = useState<StaffOperationsIntentState | null>(() => {
    const value = params.get("state");
    return value !== null && isState(value) ? value : null;
  });
  const [task, setTask] = useState<StaffOperationsTaskId | null>(() => {
    const value = params.get("task");
    return value !== null && isTask(value) ? value : null;
  });
  const [reason, setReason] = useState<StaffOperationsControlReason>("INCIDENT");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Readonly<{ kind: "ok" | "error"; text: string }> | null>(
    null,
  );
  const summary = useOperationsSummary(capabilities.read);
  const jobs = useOperationsJobs({ state, task }, capabilities.read);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include the background jobs."
      />
    );
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: operationsKeys.all });
  };

  /** Runs one action, answers in words, and reads again. A failed action says what to do and never shows an id. */
  const act = async (work: () => Promise<string>) => {
    setBusy(true);
    setNotice(null);
    try {
      setNotice({ kind: "ok", text: await work() });
      await refresh();
    } catch (error) {
      const failure = describeOperationsError(error);
      setNotice({ kind: "error", text: failure.message });
      if (failure.reload) await refresh();
    } finally {
      setBusy(false);
    }
  };

  const pause = (taskId: string, label: string) =>
    act(async () => {
      const result = await api.setJobControl(taskId, { paused: true, reasonCode: reason });
      return result.changed ? `${label} is paused.` : `${label} was paused already.`;
    });
  const resume = (taskId: string, label: string) =>
    act(async () => {
      const result = await api.setJobControl(taskId, { paused: false });
      return result.changed ? `${label} is running again.` : `${label} was not paused.`;
    });
  const requeue = (job: StaffOperationsIntent) =>
    act(async () => {
      await api.requeueJob(job.id, { reasonCode: reason });
      return `${taskLabels[job.taskId]} was queued again as a new job.`;
    });

  const rows = jobs.data?.pages.flatMap((page) => page.items) ?? [];
  const allControl = summary.data?.controls.find((control) => control.taskId === "*");
  const allPaused = allControl?.paused === true;
  const taskViews: TaskView[] = (summary.data?.tasks ?? []).map((row) => {
    const control = summary.data?.controls.find((entry) => entry.taskId === row.taskId);
    const label = taskLabels[row.taskId];
    return {
      task: row,
      ownPause: control?.paused === true,
      detail:
        control?.paused === true && control.reasonCode !== null
          ? controlReasonLabels[control.reasonCode]
          : allPaused && allControl?.reasonCode != null
            ? controlReasonLabels[allControl.reasonCode]
            : undefined,
      busy,
      onPause: () => void pause(row.taskId, label),
      onResume: () => void resume(row.taskId, label),
    };
  });
  const jobViews: JobView[] = rows.map((job) => ({
    job,
    busy,
    onRequeue: () => void requeue(job),
  }));
  // The action column exists for an administrator only (the API refuses the others; hiding is presentation).
  const visibility = { action: capabilities.manage };

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Lafayette operations"
        title="Operations"
        titleId="page-title"
        description={
          <>
            The background jobs of the platform: what waits, what runs, what failed and why nothing
            happened. {capabilities.manage ? "You can pause, resume and re-run." : "You can read."}
          </>
        }
        actions={
          <AreaLinks
            links={[
              { to: "/operations/diagnostics", label: "Diagnostics" },
              { to: "/operations/audit", label: "Audit trail" },
              { to: "/operations/runbooks/index", label: "Runbooks" },
            ]}
          />
        }
      />

      {notice === null ? null : (
        <StatusMessage
          message={{ tone: notice.kind === "ok" ? "success" : "error", text: notice.text }}
        />
      )}

      {capabilities.manage ? (
        <Card>
          <CardContent className="flex flex-wrap items-end gap-5 pt-4 sm:pt-5">
            <div className="min-w-0 max-w-[560px] flex-1 basis-72">
              <SelectField
                id="operations-reason"
                label="Reason for the next pause or re-run"
                value={reason}
                onChange={(value) => {
                  if (isReason(value)) setReason(value);
                }}
                options={staffOperationsControlReasons.map((code) => ({
                  value: code,
                  label: controlReasonLabels[code],
                }))}
                hint="Pausing stops new work of a task at once, with no deploy. Its jobs wait in Postgres and run again after a resume."
              />
            </div>
            {allPaused ? (
              <Button
                type="button"
                variant="outline"
                className="ml-auto text-base"
                disabled={busy}
                onClick={() => void resume("*", "Every task")}
              >
                <Icon name="play" />
                Resume every task
              </Button>
            ) : (
              <Button
                type="button"
                variant="danger"
                className="ml-auto text-base"
                disabled={busy}
                onClick={() => void pause("*", "Every task")}
              >
                <Icon name="pause" />
                Pause every task
              </Button>
            )}
          </CardContent>
        </Card>
      ) : null}

      <section className="grid gap-3" aria-labelledby="operations-queues">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="operations-queues" className={sectionHeading}>
            Task queues
          </h2>
          {summary.isSuccess ? (
            <Button type="button" variant="outline" size="sm" onClick={() => void refresh()}>
              <Icon name="refresh" />
              Refresh
            </Button>
          ) : null}
        </div>
        {summary.isPending ? (
          <AsyncState kind="loading" title="Loading the queues" message="Waiting for the answer." />
        ) : summary.isError ? (
          <AsyncState
            kind="error"
            title="The queues could not be read"
            message={describeOperationsError(summary.error).message}
            onRetry={() => void summary.refetch()}
          />
        ) : (
          <>
            <p className={hint} role="status">
              Updated {formatInstant(summary.data.generatedAt)}. “Oldest waiting” is the age of the
              oldest job that is due now; it is data, not a promise.
            </p>
            <DataTable<TaskView>
              caption="Background task queues"
              data={taskViews}
              getRowId={(view) => view.task.taskId}
              rowAttributes={(view) => ({ "data-task": view.task.taskId })}
              columns={taskColumns}
              banded="strong"
              columnVisibility={visibility}
            />
          </>
        )}
      </section>

      <section className="grid gap-3" aria-labelledby="operations-jobs">
        <h2 id="operations-jobs" className={sectionHeading}>
          Jobs
        </h2>
        <Card>
          <CardContent className="grid gap-4 pt-4 sm:grid-cols-2 sm:pt-5">
            <SelectField
              id="operations-state"
              label="State"
              value={state ?? ""}
              onChange={(value) => setState(isState(value) ? value : null)}
              placeholder="All states"
              options={staffOperationsIntentStates.map((code) => ({
                value: code,
                label: stateLabels[code],
              }))}
            />
            <SelectField
              id="operations-task"
              label="Task"
              value={task ?? ""}
              onChange={(value) => setTask(isTask(value) ? value : null)}
              placeholder="All tasks"
              options={staffOperationsTaskIds.map((code) => ({
                value: code,
                label: taskLabels[code],
              }))}
            />
          </CardContent>
        </Card>
        {jobs.isPending ? (
          <AsyncState kind="loading" title="Loading the jobs" message="Waiting for the answer." />
        ) : jobs.isError && jobs.data === undefined ? (
          <AsyncState
            kind="error"
            title="The jobs could not be read"
            message={describeOperationsError(jobs.error).message}
            onRetry={() => void jobs.refetch()}
          />
        ) : rows.length === 0 ? (
          <AsyncState
            kind="empty"
            title="No jobs here"
            message="No job matches this filter right now."
          />
        ) : (
          <>
            <p className={hint} role="status">
              {rows.length} {rows.length === 1 ? "job" : "jobs"} shown, newest change first.
            </p>
            <DataTable<JobView>
              caption="Background jobs"
              data={jobViews}
              getRowId={(view) => view.job.id}
              rowAttributes={(view) => ({ "data-job": view.job.id })}
              columns={jobColumns}
              className="[&_code]:text-xs [&_td]:text-[13px]"
              banded="strong"
              columnVisibility={visibility}
            />
            {jobs.hasNextPage ? (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={jobs.isFetchingNextPage}
                  onClick={() => void jobs.fetchNextPage()}
                >
                  {jobs.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </section>
    </section>
  );
}
